import React, { useState, useMemo, useCallback } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { leaveApi } from "../../../api/leaveApi";
import { SearchBar } from "../../../components/ui/SearchBar";
import { useBranchHierarchy } from "../../../api/queries/organizationQueries";
import usePermissions from "../../../hooks/usePermissions";

const EmployeeLeaveTable = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL Search Params
  // Company-wide HR start on their branch tree. Branch roles send no branch
  // by default: the backend limits them to their branches, and the login
  // account's branch can be outside them (e.g. a parent branch).
  const isCompanyWide = usePermissions().companyWide("leave");
  const storedBranchId = isCompanyWide ? localStorage.getItem("branchId") : "";

  const currentPage = Number(searchParams.get("page")) || 1;
  const itemsPerPage = Number(searchParams.get("limit")) || 10;

  // State
  const { data: allBranches = [] } = useBranchHierarchy(storedBranchId);

  const isMultipleBranches = allBranches.length > 1;

  const branchOptions = isMultipleBranches
    ? [
        { value: storedBranchId, label: "All Branch" },
        ...allBranches
          .filter(
            (branch) =>
              branch.id !== "All Branch" &&
              branch.id !== storedBranchId &&
              !/all/i.test(branch.branchName || branch.name),
          )
          .map((branch) => ({
            value: branch.id,
            label: branch.branchName || branch.name,
          })),
      ]
    : [
        ...allBranches.map((branch) => ({
          value: branch.id,
          label: branch.branchName || branch.name,
        })),
      ];

  const filterSchema = [
    { key: "name", label: "Name", type: "text", placeholder: "Search Name..." },
    // Branch roles: no list (the backend already limits them to their branches)
    ...(branchOptions.length
      ? [
          {
            key: "branch",
            label: "Branch",
            defaultValue: storedBranchId,
            type: "select",
            options: branchOptions,
          },
        ]
      : []),
  ];

  // Initialize draft filters directly from URL search params or defaults
  const [filters, setFilters] = useState(() => {
    return filterSchema.reduce((initialObj, field) => {
      const urlVal = searchParams.get(
        field.key === "branch" ? "branchId" : field.key,
      );
      initialObj[field.key] =
        urlVal !== null
          ? urlVal
          : field.defaultValue !== undefined
            ? field.defaultValue
            : "";
      return initialObj;
    }, {});
  });

  // Applied filters are derived directly from URL parameters
  const appliedFilters = useMemo(() => {
    const currentApplied = {};
    filterSchema.forEach((field) => {
      const paramKey = field.key === "branch" ? "branchId" : field.key;
      const val = searchParams.get(paramKey);
      if (val) currentApplied[paramKey] = val;
    });
    // Fallback default branch if none specified in URL
    if (!currentApplied.branchId) {
      currentApplied.branchId = storedBranchId;
    }
    return currentApplied;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, storedBranchId]);

  const queryKey = [
    "getAllEmployeeBalance",
    { ...appliedFilters, currentPage, itemsPerPage },
  ];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      leaveApi.getAllEmployeeBalance({
        ...appliedFilters,
        search: appliedFilters.name, // API param is `search` (name or employee no.)
        name: undefined,
        branchId: appliedFilters.branchId || storedBranchId || undefined,
        page: currentPage,
        limit: itemsPerPage,
      }),
    placeholderData: keepPreviousData,
  });

  const employeeListData = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data?.list || data?.data || [];
  }, [data]);

  const serverMeta = useMemo(() => {
    if (!data) return { total: 0, totalPages: 1, page: 1 };

    const rawTotal =
      data.total ?? data.meta?.total ?? data.count ?? data.pagination?.total;

    const total =
      rawTotal !== undefined ? rawTotal : Array.isArray(data) ? data.length : 0;

    const totalPages =
      data.totalPages ??
      data.meta?.totalPages ??
      data.pagination?.totalPages ??
      (Math.ceil(total / itemsPerPage) || 1);

    return {
      total: Number(total),
      totalPages:
        Number(totalPages) < currentPage ? currentPage : Number(totalPages),
      page: currentPage,
    };
  }, [data, itemsPerPage, currentPage]);

  const totalPages = serverMeta.totalPages;
  const totalItems = serverMeta.total;

  const getPaginationGroup = () => {
    const totalDisplayed = 5;
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages.filter((p) => p !== 1 && p !== totalPages);
  };

  const handleSearch = () => {
    const newParams = new URLSearchParams(searchParams);

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      const paramKey = key === "branch" ? "branchId" : key;

      if (
        value !== undefined &&
        value !== null &&
        value !== "All" &&
        value !== "both" &&
        String(value).trim() !== ""
      ) {
        newParams.set(paramKey, String(value).trim());
      } else {
        newParams.delete(paramKey);
      }
    });

    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = useCallback(
    (targetPage, overrideLimit = null) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", targetPage.toString());

      if (overrideLimit !== null && overrideLimit !== itemsPerPage) {
        newParams.set("limit", overrideLimit.toString());
        newParams.set("page", "1"); // Reset to page 1 on limit change
      }

      setSearchParams(newParams);
    },
    [searchParams, itemsPerPage, setSearchParams],
  );

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading data.</div>;

  return (
    <div className="w-100">
      <div className="mb-4">
        <h1 className="fw-bold text-dark m-0 page-title">Employee Leave</h1>
        <p className="text-muted small mb-0">
          Every employee's leave balances. Open one to see or adjust them.
        </p>
      </div>

      <SearchBar
        filters={filters}
        setFilters={setFilters}
        fields={filterSchema}
        onSearch={handleSearch}
      />

      <table className="table">
        <thead>
          <tr>
            <th>Employee Leave Details</th>
          </tr>
        </thead>
        <tbody>
          {employeeListData.map((emp) => {
            const leaveBalances = Array.isArray(emp.leaveBalance)
              ? emp.leaveBalance
              : emp.leaveBalance
                ? [emp.leaveBalance]
                : [];

            return (
              <tr key={emp.id}>
                <td colSpan="5" className="p-2 border-0">
                  <button
                    type="button"
                    className="w-100 text-start btn btn-outline-light border text-dark p-3 shadow-sm d-flex justify-content-between align-items-center bg-white transition-all"
                    onClick={() => navigate(`/leave/employee/${emp.id}`)}
                  >
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <h5 className="mb-0 fw-bold text-primary">
                          {emp.firstName || ""}{" "}
                          {emp.middleName ? `${emp.middleName[0]}. ` : ""}
                          {emp.lastName || ""} {emp.extension || ""}
                        </h5>
                        <span className="badge bg-secondary text-white font-monospace small">
                          {emp.employeeId || "N/A"}
                        </span>
                        <span
                          className={`badge ${emp.employeeStatus === "Validated" ? "bg-success" : "bg-warning"} text-white`}
                        >
                          {emp.employeeStatus || "Unassigned"}
                        </span>
                      </div>

                      <div className="text-muted small d-flex flex-wrap gap-3 mt-1">
                        <span>
                          <strong>Branch:</strong>{" "}
                          {emp.branch?.branchName || "Unassigned"}
                        </span>
                        <span>
                          <strong>Email:</strong>{" "}
                          {emp.companyEmail || emp.personalEmail || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-4">
                      <div className="text-end border-start ps-3 min-w-320px">
                        <span className="d-block small text-muted fw-semibold mb-1 text-center">
                          Leave Balances
                        </span>
                        {leaveBalances.length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {leaveBalances.map((lb, index) => (
                              <div
                                key={lb.id || index}
                                className="small bg-light px-2 py-1 rounded border d-flex justify-content-between align-items-center"
                              >
                                <span
                                  className="fw-bold text-dark text-truncate mw-110px"
                                  title={lb.leaveType?.name || "Leave"}
                                >
                                  {lb.leaveType?.name || "Leave"}:
                                </span>
                                <div className="d-flex gap-2">
                                  <span className="text-success">
                                    Alloc:{" "}
                                    <strong>{lb.allocated ?? "0"}</strong>
                                  </span>
                                  <span className="text-success">
                                    Booked: <strong>{lb.booked ?? "0"}</strong>
                                  </span>
                                  <span className="text-danger">
                                    Used: <strong>{lb.used ?? "0"}</strong>
                                  </span>
                                  <span className="text-primary">
                                    Pend: <strong>{lb.pending ?? "0"}</strong>
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="small text-muted fst-italic text-center py-2 bg-light rounded border">
                            No leave balance data
                          </div>
                        )}
                      </div>

                      <div className="btn btn-sm btn-primary px-3">
                        View &rarr;
                      </div>
                    </div>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted fs-075">Show</span>
          <select
            className="items-select"
            value={itemsPerPage}
            onChange={(e) => handlePageChange(1, Number(e.target.value))}
          >
            {[10, 25, 50].map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <span className="text-muted fs-075">
            of <strong>{(totalItems || 0).toLocaleString()}</strong> entries
          </span>
        </div>

        <nav className="d-flex align-items-center">
          <button
            type="button"
            className={`page-link-custom ${Number(currentPage) <= 1 ? "disabled" : ""}`}
            onClick={() => {
              const prevNum = Number(currentPage) - 1;
              if (prevNum >= 1) handlePageChange(prevNum);
            }}
            disabled={Number(currentPage) <= 1}
          >
            <i className="bi bi-chevron-left fs-080 icon-stroke"></i>
          </button>

          <button
            type="button"
            className={`page-link-custom ${Number(currentPage) === 1 ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>

          {Number(currentPage) > 3 && totalPages > 5 && (
            <span className="mx-2 text-muted">...</span>
          )}

          {getPaginationGroup().map((p) => {
            if (p === 1 || p === totalPages) return null;
            return (
              <button
                type="button"
                key={p}
                className={`page-link-custom ${Number(currentPage) === p ? "active" : ""}`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            );
          })}

          {Number(currentPage) < totalPages - 2 && totalPages > 5 && (
            <span className="mx-2 text-muted">...</span>
          )}

          {totalPages > 1 && (
            <button
              type="button"
              className={`page-link-custom ${Number(currentPage) === totalPages ? "active" : ""}`}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          )}

          <button
            type="button"
            className={`page-link-custom ${Number(currentPage) >= totalPages ? "disabled" : ""}`}
            onClick={() => {
              const nextNum = Number(currentPage) + 1;
              if (nextNum <= totalPages) handlePageChange(nextNum);
            }}
            disabled={Number(currentPage) >= totalPages}
          >
            <i className="bi bi-chevron-right fs-080 icon-stroke"></i>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default EmployeeLeaveTable;
